import React from "react";
import {
  Box,
  Paper,
  Typography,
  TextField,
  Stack,
  Divider,
  Button,
  Switch,
  FormControlLabel,
  Chip,
  IconButton,
  Tooltip,
  alpha,
  useMediaQuery,
} from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import axiosInstance from "../../utils/axiosInstance";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import { DRAWER_WIDTH_LG, DRAWER_WIDTH_MD } from "../../App";
import {
  EMAIL_API_KEY,
  FIREBASE_BASE_URL,
} from "../feature-configurator/utils/constants";
import { useEntitlements } from "../../services/entitlement/useEntitlement";

function safeStr(v: unknown) {
  return typeof v === "string" ? v : v == null ? "" : String(v);
}
function safeBool(v: unknown) {
  return v === true;
}

function parseEntitlementDenied(e: any): {
  denied: boolean;
  resource?: string;
} {
  const status = e?.response?.status;
  const msg = String(e?.response?.data?.message || "");

  if (status !== 403) return { denied: false };

  const needle = "Entitlement denied for resource:";
  if (!msg.includes(needle)) return { denied: false };

  const resource = msg.split(needle)[1]?.trim() || undefined;
  return { denied: true, resource };
}

function getErrorMessage(e: any) {
  const data = e?.response?.data;

  if (data?.message && typeof data.message === "string") return data.message;
  if (data?.error && typeof data.error === "string") return data.error;

  if (data && typeof data === "object") {
    try {
      return JSON.stringify(data);
    } catch {
      // ignore
    }
  }

  return safeStr(e?.message || "Something went wrong.");
}

const requestConfig = {
  headers: {
    emailApiKey: EMAIL_API_KEY,
    "Content-Type": "application/json",
  },
};

type PracticeGroupApi = {
  kinshipId: string;
  code: string;
  name: string;
  urlCode: string;
  multiClinicianBooking?: boolean;
  vetstoriaApiVersion?: string;
  externalName?: string;
  quickBookEnabled?: boolean;
  crmName?: string;
  ssoEnabled?: boolean;
  active?: boolean;
  partner?: string | null;
  practiceGroupId?: string | null;
};

type OohPhone = { label?: string | null; phoneNumber?: string | null };

type PracticeApi = {
  kinshipId: string;
  code: string;
  name: string;
  phone?: string | null;
  email?: string | null;
  address?: string | null;
  vetstoriaClinicHash?: string | null;
  vetstoriaLocationId?: string | null;
  hours?: string | null;
  oohService?: string | null;
  oohPhoneNumbers?: OohPhone[];
  autoReplyEnabled?: boolean | null;
  autoReplyMessage?: string | null;
  ssoEnabled?: boolean | null;
  active?: boolean | null;
  practiceGroupKinshipId?: string | null;
  externalName?: string | null;

  vetstoriaApiVersion?: string | null;
  quickBookEnabled?: boolean | null;
  crmName?: string | null;
  practiceGroupId?: string | null;
  multiClinicianBooking?: boolean | null;
};

type EditModel = {
  url_code: string;
  partner: string;
  practice_group_name: string;
  practice_group_id: string;
  multi_clinician_booking: boolean;
  vetstoria_api_version: string;
  external_name: string;
  quick_book_enabled: boolean;
  crm_name: string;
  sso_enabled: boolean;
  active: boolean;

  practice_location_name: string;
  practice_location_address: string;
  practice_location_phone: string;
  practice_location_email: string;
  practice_location_opening_hours: string;

  ooh_service: string;
  ooh_phone_numbers: Array<{ label: string; phoneNumber: string }>;

  vetstoria_clinic_hash: string;
  vetstoria_location_id: string;

  auto_reply_enabled: boolean;
  auto_reply_message: string;

  practice_kinship_id: string;
  practice_group_kinship_id: string;
};

type OohPhoneNormalized = { label: string; phoneNumber: string };

const PRACTICE_FIELDS = new Set<keyof EditModel>([
  "practice_location_name",
  "practice_location_address",
  "practice_location_phone",
  "practice_location_email",
  "practice_location_opening_hours",
  "ooh_service",
  "ooh_phone_numbers",
  "vetstoria_clinic_hash",
  "vetstoria_location_id",
  "auto_reply_enabled",
  "auto_reply_message",
  "sso_enabled",
  "active",
  "external_name",
]);

const GROUP_FIELDS = new Set<keyof EditModel>([
  "url_code",
  "partner",
  "practice_group_name",
  "practice_group_id",
  "multi_clinician_booking",
  "vetstoria_api_version",
  "quick_book_enabled",
  "crm_name",
  "sso_enabled",
  "active",
]);

function normalizeOohPhones(list?: OohPhone[]) {
  return (list ?? []).map(p => ({
    label: safeStr(p.label).trim(),
    phoneNumber: safeStr(p.phoneNumber).trim(),
  }));
}

function formatOohList(list: OohPhoneNormalized[] | undefined) {
  const arr = list ?? [];
  if (arr.length === 0) return "—";
  return arr
    .map(p => {
      const label = (p.label || "").trim();
      const phone = (p.phoneNumber || "").trim();
      if (label && phone) return `${label}: ${phone}`;
      return phone || label || "—";
    })
    .join(" • ");
}

function renderChangeValue(field: keyof EditModel, value: any) {
  if (field === "ooh_phone_numbers")
    return formatOohList(value as OohPhoneNormalized[]);
  if (typeof value === "boolean") return value ? "true" : "false";
  if (value == null || value === "") return "—";
  return String(value);
}

function diffObject(before: EditModel, after: EditModel) {
  const changes: Array<{ field: keyof EditModel; from: any; to: any }> = [];
  (Object.keys(after) as Array<keyof EditModel>).forEach(k => {
    const a = after[k];
    const b = before[k];

    if (k === "ooh_phone_numbers") {
      const sa = JSON.stringify(a);
      const sb = JSON.stringify(b);
      if (sa !== sb) changes.push({ field: k, from: b, to: a });
      return;
    }

    if (a !== b) changes.push({ field: k, from: b, to: a });
  });
  return changes;
}

function buildPatchFromChanges(
  _initial: EditModel,
  model: EditModel,
  changes: Array<{ field: keyof EditModel }>,
  allowed: Set<keyof EditModel>,
) {
  const patch: Partial<Record<keyof EditModel, unknown>> = {};
  for (const c of changes) {
    const key = c.field;
    if (!allowed.has(key)) continue;
    patch[key] = model[key];
  }
  return patch;
}

const shortId = (id: string) =>
  id.length > 18 ? `${id.slice(0, 8)}…${id.slice(-4)}` : id;

export default function PracticeConfigToolEditPage() {
  const { practiceKinshipId } = useParams<{ practiceKinshipId: string }>();
  const navigate = useNavigate();
  const isMobile = useMediaQuery("(max-width:600px)");
  const { loaded: entitlementsLoaded, can } = useEntitlements();

  const [loading, setLoading] = React.useState(true);
  const [saving, setSaving] = React.useState(false);

  const [initial, setInitial] = React.useState<EditModel | null>(null);
  const [model, setModel] = React.useState<EditModel | null>(null);

  const [successMessage, setSuccessMessage] = React.useState<string>("");

  // Separate "permission denied" from generic errors so UI can be accurate.
  const [permissionDenied, setPermissionDenied] = React.useState(false);
  const [deniedResource, setDeniedResource] = React.useState<
    string | undefined
  >(undefined);

  const [error, setError] = React.useState<string>("");

  const showNoPermission = (msg: string, resource?: string) => {
    setPermissionDenied(true);
    setDeniedResource(resource);
    setError(msg);
  };

  const changes = React.useMemo(() => {
    if (!initial || !model) return [];
    return diffObject(initial, model);
  }, [initial, model]);

  const setField = <K extends keyof EditModel>(key: K, value: EditModel[K]) => {
    setModel(prev => (prev ? { ...prev, [key]: value } : prev));
  };

  const addOohPhone = () => {
    setModel(prev => {
      if (!prev) return prev;
      return {
        ...prev,
        ooh_phone_numbers: [
          ...prev.ooh_phone_numbers,
          { label: "", phoneNumber: "" },
        ],
      };
    });
  };

  const updateOohPhone = (
    idx: number,
    patch: Partial<{ label: string; phoneNumber: string }>,
  ) => {
    setModel(prev => {
      if (!prev) return prev;
      const next = prev.ooh_phone_numbers.slice();
      next[idx] = { ...next[idx], ...patch };
      return { ...prev, ooh_phone_numbers: next };
    });
  };

  const removeOohPhone = (idx: number) => {
    setModel(prev => {
      if (!prev) return prev;
      const next = prev.ooh_phone_numbers.slice();
      next.splice(idx, 1);
      return { ...prev, ooh_phone_numbers: next };
    });
  };

  const load = React.useCallback(async () => {
    if (!practiceKinshipId) return;
    setLoading(true);
    setError("");
    setPermissionDenied(false);
    setDeniedResource(undefined);

    try {
      const practiceRes = await axiosInstance.get<PracticeApi[]>(
        `${FIREBASE_BASE_URL}/vetinsight/practices`,
        requestConfig,
      );

      const practice = (practiceRes.data ?? []).find(
        p => p.kinshipId === practiceKinshipId,
      );

      if (!practice) {
        setError("Practice location not found.");
        setInitial(null);
        setModel(null);
        return;
      }

      const groupsRes = await axiosInstance.get<PracticeGroupApi[]>(
        `${FIREBASE_BASE_URL}/vetinsight/practice_groups`,
        requestConfig,
      );

      const group = (groupsRes.data ?? []).find(
        g => g.kinshipId === practice.practiceGroupKinshipId,
      );

      const merged: EditModel = {
        url_code: safeStr(group?.urlCode ?? group?.code ?? ""),
        partner: safeStr(group?.partner ?? ""),
        practice_group_name: safeStr(group?.name ?? ""),
        practice_group_id: safeStr(group?.practiceGroupId ?? ""),
        multi_clinician_booking: safeBool(group?.multiClinicianBooking),
        vetstoria_api_version: safeStr(group?.vetstoriaApiVersion ?? ""),
        external_name: safeStr(
          practice.externalName ?? group?.externalName ?? "",
        ),
        quick_book_enabled: safeBool(group?.quickBookEnabled),
        crm_name: safeStr(group?.crmName ?? ""),
        sso_enabled: safeBool(practice.ssoEnabled ?? group?.ssoEnabled),
        active:
          practice.active !== null && practice.active !== undefined
            ? safeBool(practice.active)
            : safeBool(group?.active),

        practice_location_name: safeStr(practice.name),
        practice_location_address: safeStr(practice.address),
        practice_location_phone: safeStr(practice.phone),
        practice_location_email: safeStr(practice.email),
        practice_location_opening_hours: safeStr(practice.hours),

        ooh_service: safeStr(practice.oohService),
        ooh_phone_numbers: normalizeOohPhones(practice.oohPhoneNumbers),

        vetstoria_clinic_hash: safeStr(practice.vetstoriaClinicHash),
        vetstoria_location_id: safeStr(practice.vetstoriaLocationId),

        auto_reply_enabled: safeBool(practice.autoReplyEnabled),
        auto_reply_message: safeStr(practice.autoReplyMessage),

        practice_kinship_id: safeStr(practice.kinshipId),
        practice_group_kinship_id: safeStr(
          practice.practiceGroupKinshipId ?? group?.kinshipId ?? "",
        ),
      };

      setInitial(merged);
      setModel(merged);
    } catch (e: any) {
      const ent = parseEntitlementDenied(e);
      if (ent.denied) {
        showNoPermission(
          "You don’t have permission to view Practice Config Tool details. Ask an admin to grant create_practice_tool:get.",
          ent.resource,
        );
        setInitial(null);
        setModel(null);
        return;
      }

      setError(getErrorMessage(e) || "Failed to load practice config.");
      setInitial(null);
      setModel(null);
    } finally {
      setLoading(false);
    }
  }, [practiceKinshipId]);

  React.useEffect(() => {
    void load();
  }, [load]);

  const handleCancel = () => navigate(-1);
  const API_BASE = process.env.REACT_APP_FIREBASE_BASE_URL;

  const handleSave = async () => {
    if (!model || !initial) return;
    if (changes.length === 0) return;

    if (!entitlementsLoaded) {
      showNoPermission("Checking your access… please try again in a moment.");
      return;
    }

    if (!can("create_practice_tool:patch")) {
      showNoPermission(
        "You don’t have permission to edit Practice Config Tool settings. Ask an admin to grant create_practice_tool:patch.",
        "create_practice_tool:patch",
      );
      return;
    }

    setSaving(true);
    setError("");
    setPermissionDenied(false);
    setDeniedResource(undefined);

    try {
      const practicePatch = buildPatchFromChanges(
        initial,
        model,
        changes,
        PRACTICE_FIELDS,
      );

      const groupPatch = buildPatchFromChanges(
        initial,
        model,
        changes,
        GROUP_FIELDS,
      );

      const calls: Promise<any>[] = [];

      if (Object.keys(practicePatch).length > 0) {
        calls.push(
          axiosInstance.put(
            `${API_BASE}/vetinsight/practices/${model.practice_kinship_id}`,
            practicePatch,
            requestConfig,
          ),
        );
      }

      if (Object.keys(groupPatch).length > 0) {
        calls.push(
          axiosInstance.put(
            `${API_BASE}/vetinsight/practice_groups/${model.practice_group_kinship_id}`,
            groupPatch,
            requestConfig,
          ),
        );
      }

      const results = await Promise.allSettled(calls);

      const failed = results.find(r => r.status === "rejected");
      if (failed) {
        const reason = (failed as PromiseRejectedResult).reason;

        const ent = parseEntitlementDenied(reason);
        if (ent.denied) {
          showNoPermission(
            "You don’t have permission to save Practice Config Tool changes. Ask an admin to grant create_practice_tool:patch.",
            ent.resource,
          );
          return;
        }

        setError(getErrorMessage(reason));

        const okCount = results.filter(r => r.status === "fulfilled").length;
        if (okCount > 0) {
          setError(
            prev => `Partial save: ${okCount} update(s) succeeded. ${prev}`,
          );
        }
        return;
      }

      setInitial(model);
      setSuccessMessage("Practice configuration updated successfully.");

      setTimeout(() => {
        navigate(-1);
      }, 3000);
    } catch (e: any) {
      const ent = parseEntitlementDenied(e);
      if (ent.denied) {
        showNoPermission(
          "You don’t have permission to save Practice Config Tool changes. Ask an admin to grant create_practice_tool:patch.",
          ent.resource,
        );
        return;
      }

      setError(getErrorMessage(e));
    } finally {
      setSaving(false);
    }
  };

  const pageX = { xs: 3, sm: 3, md: 4 };
  const pageMax = { xs: "100%", sm: 900, md: 1200, lg: 1440, xl: 1600 };

  if (loading) {
    return (
      <Box sx={{ width: "100%", overflowX: "hidden" }}>
        <Box sx={{ mx: "auto", maxWidth: pageMax, px: pageX, py: 2 }}>
          <Paper
            elevation={0}
            sx={{ p: 3, borderRadius: 3, border: "1px solid #E5E7EB" }}
          >
            <Typography fontWeight={800}>Loading…</Typography>
          </Paper>
        </Box>
      </Box>
    );
  }

  if (permissionDenied) {
    return (
      <Box sx={{ width: "100%", overflowX: "hidden" }}>
        <Box sx={{ mx: "auto", maxWidth: pageMax, px: pageX, py: 2 }}>
          <Paper
            elevation={0}
            sx={{
              p: 3,
              borderRadius: 3,
              border: "1px solid #FCA5A5",
              background: alpha("#FEF2F2", 0.98),
            }}
          >
            <Typography fontWeight={900} sx={{ color: "#991B1B", mb: 0.5 }}>
              Access denied
            </Typography>

            <Typography variant="body2" sx={{ color: "#7F1D1D" }}>
              {error ||
                "You don’t have permission to access Practice Config Tool."}
            </Typography>

            {deniedResource ? (
              <Typography variant="body2" sx={{ color: "#7F1D1D", mt: 1 }}>
                Missing permission: <code>{deniedResource}</code>
              </Typography>
            ) : null}

            <Box sx={{ mt: 2 }}>
              <Button variant="outlined" onClick={() => navigate(-1)}>
                Back
              </Button>
            </Box>
          </Paper>
        </Box>
      </Box>
    );
  }

  if (!model) {
    return (
      <Box sx={{ width: "100%", overflowX: "hidden" }}>
        <Box sx={{ mx: "auto", maxWidth: pageMax, px: pageX, py: 2 }}>
          <Paper
            elevation={0}
            sx={{ p: 3, borderRadius: 3, border: "1px solid #E5E7EB" }}
          >
            <Typography fontWeight={900} sx={{ mb: 1 }}>
              Practice Config Tool — Edit
            </Typography>
            <Typography sx={{ color: "#6B7280" }}>
              {error || "No data."}
            </Typography>
            <Box sx={{ mt: 2 }}>
              <Button variant="outlined" onClick={() => navigate(-1)}>
                Back
              </Button>
            </Box>
          </Paper>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ width: "100%", overflowX: "hidden", pb: "120px" }}>
      <Box
        sx={{
          mx: "auto",
          maxWidth: pageMax,
          px: { xs: 0, sm: 3 },
          pt: 2,
          pb: { xs: 14, sm: 12 },
          minWidth: 0,
        }}
      >
        <Paper
          elevation={0}
          sx={{
            borderRadius: 4,
            p: { xs: 2, sm: 2.5, md: 3 },
            mb: 2,
            background: "#FFFFFF",
            border: "1px solid #E5E7EB",
            boxShadow: "0 6px 18px rgba(0,0,0,0.04)",
            minWidth: 0,
          }}
        >
          <Stack direction="column" spacing={1} sx={{ minWidth: 0 }}>
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="h5" fontWeight={900} sx={{ mb: 0.5 }}>
                Edit Practice Configuration
              </Typography>
              <Typography variant="body2" sx={{ color: "#6B7280" }}>
                Update practice group + location fields. Changes will be audited
                on save.
              </Typography>

              <Stack
                direction="row"
                spacing={1}
                sx={{ mt: 1, flexWrap: "wrap", rowGap: 1, minWidth: 0 }}
              >
                <Tooltip title={model.practice_kinship_id}>
                  <Chip
                    size="small"
                    label={`Practice: ${shortId(model.practice_kinship_id)}`}
                    sx={{
                      maxWidth: "100%",
                      "& .MuiChip-label": {
                        maxWidth: "100%",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      },
                    }}
                  />
                </Tooltip>

                <Tooltip title={model.practice_group_kinship_id}>
                  <Chip
                    size="small"
                    label={`Group: ${shortId(model.practice_group_kinship_id)}`}
                    sx={{
                      maxWidth: "100%",
                      "& .MuiChip-label": {
                        maxWidth: "100%",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      },
                    }}
                  />
                </Tooltip>
              </Stack>
            </Box>

            {error && !permissionDenied ? (
              <Typography sx={{ mt: 1, color: "#B91C1C", fontWeight: 700 }}>
                {error}
              </Typography>
            ) : null}
          </Stack>
        </Paper>

        <Stack spacing={2} sx={{ minWidth: 0 }}>
          {/* Practice Group */}
          <Paper
            elevation={0}
            sx={{
              p: { xs: 2, sm: 3 },
              borderRadius: 4,
              border: "1px solid #E5E7EB",
              background: "#FFFFFF",
              minWidth: 0,
            }}
          >
            <Typography fontWeight={900} sx={{ mb: 1 }}>
              Practice Group
            </Typography>
            <Divider sx={{ mb: 2 }} />

            <Stack
              spacing={2}
              direction={{ xs: "column", md: "row" }}
              sx={{ minWidth: 0 }}
            >
              <TextField
                label="Practice Group Name"
                value={model.practice_group_name}
                onChange={e => setField("practice_group_name", e.target.value)}
                fullWidth
              />
              <TextField
                label="Partner"
                value={model.partner}
                onChange={e => setField("partner", e.target.value)}
                fullWidth
              />
            </Stack>

            <Stack
              spacing={2}
              direction={{ xs: "column", md: "row" }}
              sx={{ mt: 2, minWidth: 0 }}
            >
              <TextField
                label="Practice Group Code (url_code)"
                value={model.url_code}
                onChange={e => setField("url_code", e.target.value)}
                fullWidth
              />
              <TextField
                label="practice_group_id"
                value={model.practice_group_id}
                onChange={e => setField("practice_group_id", e.target.value)}
                fullWidth
              />
            </Stack>

            <Stack
              spacing={2}
              direction={{ xs: "column", lg: "row" }}
              sx={{ mt: 2, minWidth: 0 }}
            >
              <TextField
                label="vetstoria_api_version"
                value={model.vetstoria_api_version}
                onChange={e =>
                  setField("vetstoria_api_version", e.target.value)
                }
                fullWidth
              />
              <TextField
                label="crm_name"
                value={model.crm_name}
                onChange={e => setField("crm_name", e.target.value)}
                fullWidth
              />
              <TextField
                label="external_name"
                value={model.external_name}
                onChange={e => setField("external_name", e.target.value)}
                fullWidth
              />
            </Stack>

            <Stack
              spacing={1}
              direction="row"
              sx={{ mt: 2, flexWrap: "wrap", rowGap: 1 }}
            >
              <FormControlLabel
                control={
                  <Switch
                    checked={model.multi_clinician_booking}
                    onChange={e =>
                      setField("multi_clinician_booking", e.target.checked)
                    }
                  />
                }
                label="multi_clinician_booking"
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={model.quick_book_enabled}
                    onChange={e =>
                      setField("quick_book_enabled", e.target.checked)
                    }
                  />
                }
                label="quick_book_enabled"
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={model.sso_enabled}
                    onChange={e => setField("sso_enabled", e.target.checked)}
                  />
                }
                label="sso_enabled"
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={model.active}
                    onChange={e => setField("active", e.target.checked)}
                  />
                }
                label="active"
              />
            </Stack>
          </Paper>

          {/* Practice Location */}
          <Paper
            elevation={0}
            sx={{
              p: { xs: 2, sm: 3 },
              borderRadius: 4,
              border: "1px solid #E5E7EB",
              background: "#FFFFFF",
              minWidth: 0,
            }}
          >
            <Typography fontWeight={900} sx={{ mb: 1 }}>
              Practice Location
            </Typography>
            <Divider sx={{ mb: 2 }} />

            <Stack
              spacing={2}
              direction={{ xs: "column", lg: "row" }}
              sx={{ minWidth: 0 }}
            >
              <TextField
                label="Practice Location Name"
                value={model.practice_location_name}
                onChange={e =>
                  setField("practice_location_name", e.target.value)
                }
                fullWidth
              />
              <TextField
                label="Vetstoria Location ID"
                value={model.vetstoria_location_id}
                onChange={e =>
                  setField("vetstoria_location_id", e.target.value)
                }
                fullWidth
              />
              <TextField
                label="Vetstoria Clinic Hash"
                value={model.vetstoria_clinic_hash}
                onChange={e =>
                  setField("vetstoria_clinic_hash", e.target.value)
                }
                fullWidth
              />
            </Stack>

            <Stack
              spacing={2}
              direction={{ xs: "column", md: "row" }}
              sx={{ mt: 2, minWidth: 0 }}
            >
              <TextField
                label="Practice Location Email"
                value={model.practice_location_email}
                onChange={e =>
                  setField("practice_location_email", e.target.value)
                }
                fullWidth
              />
              <TextField
                label="Practice Location Phone"
                value={model.practice_location_phone}
                onChange={e =>
                  setField("practice_location_phone", e.target.value)
                }
                fullWidth
              />
            </Stack>

            <TextField
              label="Practice Location Address"
              value={model.practice_location_address}
              onChange={e =>
                setField("practice_location_address", e.target.value)
              }
              fullWidth
              multiline
              minRows={2}
              sx={{ mt: 2 }}
            />

            <TextField
              label="Practice Location Opening Hours"
              value={model.practice_location_opening_hours}
              onChange={e =>
                setField("practice_location_opening_hours", e.target.value)
              }
              fullWidth
              multiline
              minRows={3}
              sx={{ mt: 2 }}
            />

            <TextField
              label="OOH Service"
              value={model.ooh_service}
              onChange={e => setField("ooh_service", e.target.value)}
              fullWidth
              sx={{ mt: 2 }}
            />

            <Divider sx={{ my: 2 }} />

            <Box sx={{ width: "100%", minWidth: 0 }}>
              <Stack
                direction={{ xs: "column", sm: "row" }}
                alignItems={{ sm: "center" }}
                justifyContent="space-between"
                spacing={1}
                sx={{ mb: 1, minWidth: 0 }}
              >
                <Box sx={{ minWidth: 0 }}>
                  <Typography fontWeight={900} sx={{ lineHeight: 1.1 }}>
                    OOH Phone Numbers
                  </Typography>
                  <Typography variant="caption" sx={{ color: "#6B7280" }}>
                    Add one or more out-of-hours contacts. Each entry can have a
                    label.
                  </Typography>
                </Box>

                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<AddRoundedIcon />}
                  onClick={addOohPhone}
                  sx={{
                    alignSelf: { xs: "flex-start", sm: "center" },
                    borderColor: alpha("#0EA5E9", 0.35),
                    color: "#0EA5E9",
                    "&:hover": {
                      borderColor: "#0EA5E9",
                      background: alpha("#0EA5E9", 0.06),
                    },
                  }}
                >
                  Add number
                </Button>
              </Stack>

              <Stack
                spacing={1.25}
                sx={{
                  width: "100%",
                  minWidth: 0,
                  maxWidth: { lg: 620 },
                }}
              >
                {model.ooh_phone_numbers.length === 0 ? (
                  <Paper
                    variant="outlined"
                    sx={{
                      p: 2,
                      borderRadius: 3,
                      borderStyle: "dashed",
                      borderColor: alpha("#0EA5E9", 0.25),
                      background: alpha("#0EA5E9", 0.03),
                    }}
                  >
                    <Typography fontWeight={800} sx={{ mb: 0.25 }}>
                      No OOH numbers configured
                    </Typography>
                    <Typography variant="body2" sx={{ color: "#6B7280" }}>
                      Click <strong>Add number</strong> to create one.
                    </Typography>
                  </Paper>
                ) : null}

                {model.ooh_phone_numbers.map((p, idx) => (
                  <Paper
                    key={`${model.practice_kinship_id}-ooh-${idx}`}
                    variant="outlined"
                    sx={{
                      p: 1.5,
                      borderRadius: 3,
                      borderColor: alpha("#0EA5E9", 0.22),
                      background: "#FFFFFF",
                      width: "100%",
                      minWidth: 0,
                    }}
                  >
                    <Stack spacing={1} sx={{ minWidth: 0 }}>
                      <Stack
                        direction="row"
                        alignItems="center"
                        justifyContent="space-between"
                      >
                        <Typography
                          variant="caption"
                          sx={{ color: "#6B7280", fontWeight: 700 }}
                        >
                          #{idx + 1}
                        </Typography>

                        <Tooltip title="Remove this number">
                          <IconButton
                            onClick={() => removeOohPhone(idx)}
                            size="small"
                            sx={{
                              borderRadius: 2,
                              border: `1px solid ${alpha("#EF4444", 0.25)}`,
                              color: "#EF4444",
                              "&:hover": { background: alpha("#EF4444", 0.06) },
                            }}
                          >
                            <DeleteOutlineRoundedIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Stack>

                      <Stack
                        direction={{ xs: "column", sm: "row" }}
                        spacing={1}
                        sx={{ minWidth: 0 }}
                      >
                        <TextField
                          label="Label"
                          value={p.label}
                          onChange={e =>
                            updateOohPhone(idx, { label: e.target.value })
                          }
                          fullWidth
                          size="small"
                          sx={{ minWidth: 0 }}
                        />
                        <TextField
                          label="Phone"
                          value={p.phoneNumber}
                          onChange={e =>
                            updateOohPhone(idx, { phoneNumber: e.target.value })
                          }
                          fullWidth
                          size="small"
                          sx={{ minWidth: 0 }}
                        />
                      </Stack>
                    </Stack>
                  </Paper>
                ))}
              </Stack>
            </Box>

            <Divider sx={{ my: 2 }} />

            <FormControlLabel
              control={
                <Switch
                  checked={model.auto_reply_enabled}
                  onChange={e =>
                    setField("auto_reply_enabled", e.target.checked)
                  }
                />
              }
              label="auto_reply_enabled"
            />

            <TextField
              label="auto_reply_message"
              value={model.auto_reply_message}
              onChange={e => setField("auto_reply_message", e.target.value)}
              fullWidth
              multiline
              minRows={3}
              sx={{ mt: 1.5 }}
              disabled={!model.auto_reply_enabled}
            />
          </Paper>

          {/* Pending Changes */}
          <Paper
            elevation={0}
            sx={{
              p: { xs: 2, sm: 3 },
              borderRadius: 4,
              border: "1px solid #E5E7EB",
              background: "#FFFFFF",
              minWidth: 0,
            }}
          >
            <Typography fontWeight={900} sx={{ mb: 1 }}>
              Pending Changes
            </Typography>
            <Typography variant="body2" sx={{ color: "#6B7280", mb: 2 }}>
              Review what will be saved (and audited).
            </Typography>

            {changes.length === 0 ? (
              <Typography sx={{ color: "#6B7280" }}>No changes yet.</Typography>
            ) : (
              <Stack spacing={1}>
                {changes.map(c => (
                  <Paper
                    key={String(c.field)}
                    variant="outlined"
                    sx={{
                      p: 1.5,
                      borderRadius: 3,
                      borderColor: alpha("#F59E0B", 0.35),
                      background: alpha("#F59E0B", 0.06),
                      overflow: "hidden",
                    }}
                  >
                    <Typography
                      fontWeight={900}
                      sx={{ mb: 0.5, overflowWrap: "anywhere" }}
                    >
                      {String(c.field)}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ color: "#374151", overflowWrap: "anywhere" }}
                    >
                      <strong>From:</strong>{" "}
                      {renderChangeValue(c.field, c.from)}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ color: "#374151", overflowWrap: "anywhere" }}
                    >
                      <strong>To:</strong> {renderChangeValue(c.field, c.to)}
                    </Typography>
                  </Paper>
                ))}
              </Stack>
            )}
          </Paper>
        </Stack>
      </Box>

      {successMessage ? (
        <Box
          sx={{
            position: "fixed",
            left: {
              xs: 0,
              md: `${DRAWER_WIDTH_MD}px`,
              lg: `${DRAWER_WIDTH_LG}px`,
            },
            right: 0,
            width: {
              xs: "100%",
              md: `calc(100% - ${DRAWER_WIDTH_MD}px)`,
              lg: `calc(100% - ${DRAWER_WIDTH_LG}px)`,
            },
            bottom: { xs: 84, sm: 78 },
            zIndex: 1400,
            display: "flex",
            justifyContent: "center",
            px: { xs: 2, sm: 3 },
          }}
        >
          <Paper
            elevation={0}
            sx={{
              width: "100%",
              maxWidth: pageMax,
              borderRadius: 3,
              border: "1px solid #86EFAC",
              background: alpha("#ECFDF5", 0.98),
              boxShadow: "0 12px 30px rgba(0,0,0,0.12)",
              p: 1.5,
            }}
          >
            <Typography fontWeight={900} sx={{ color: "#065F46" }}>
              Success
            </Typography>
            <Typography variant="body2" sx={{ color: "#064E3B" }}>
              {successMessage}
            </Typography>
          </Paper>
        </Box>
      ) : null}

      {error ? (
        <Box
          sx={{
            position: "fixed",
            left: {
              xs: 0,
              md: `${DRAWER_WIDTH_MD}px`,
              lg: `${DRAWER_WIDTH_LG}px`,
            },
            right: 0,
            width: {
              xs: "100%",
              md: `calc(100% - ${DRAWER_WIDTH_MD}px)`,
              lg: `calc(100% - ${DRAWER_WIDTH_LG}px)`,
            },
            bottom: { xs: 84, sm: 78 },
            zIndex: 1400,
            display: "flex",
            justifyContent: "center",
            px: { xs: 2, sm: 3 },
            pointerEvents: "none",
          }}
        >
          <Paper
            elevation={0}
            sx={{
              pointerEvents: "auto",
              width: "100%",
              maxWidth: pageMax,
              borderRadius: 3,
              border: "1px solid #FCA5A5",
              background: alpha("#FEF2F2", 0.98),
              boxShadow: "0 12px 30px rgba(0,0,0,0.12)",
              p: 1.5,
            }}
          >
            <Stack direction="row" spacing={1} alignItems="flex-start">
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography fontWeight={900} sx={{ color: "#991B1B" }}>
                  {permissionDenied ? "Access denied" : "Error"}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ color: "#7F1D1D", overflowWrap: "anywhere" }}
                >
                  {error}
                </Typography>

                {permissionDenied && deniedResource ? (
                  <Typography
                    variant="body2"
                    sx={{ color: "#7F1D1D", mt: 0.75 }}
                  >
                    Missing permission: <code>{deniedResource}</code>
                  </Typography>
                ) : null}
              </Box>

              <Button
                size="small"
                variant="outlined"
                onClick={() => {
                  setError("");
                  setPermissionDenied(false);
                  setDeniedResource(undefined);
                }}
                sx={{
                  borderColor: alpha("#EF4444", 0.4),
                  color: "#991B1B",
                  "&:hover": {
                    borderColor: "#EF4444",
                    background: alpha("#EF4444", 0.06),
                  },
                }}
              >
                Dismiss
              </Button>
            </Stack>
          </Paper>
        </Box>
      ) : null}

      <Box
        sx={{
          position: "fixed",
          bottom: -8,
          zIndex: 1200,
          left: {
            xs: 0,
            md: `${DRAWER_WIDTH_MD}px`,
            lg: `${DRAWER_WIDTH_LG}px`,
          },
          right: 0,
          width: {
            xs: "100%",
            md: `calc(100% - ${DRAWER_WIDTH_MD}px)`,
            lg: `calc(100% - ${DRAWER_WIDTH_LG}px)`,
          },
          display: "flex",
          justifyContent: "center",
          px: { xs: 0, sm: 3 },
          pb: 1,
        }}
      >
        <Box sx={{ width: "100%", maxWidth: pageMax }}>
          <Paper
            elevation={0}
            sx={{
              border: "1px solid #E5E7EB",
              background: alpha("#FFFFFF", 0.92),
              backdropFilter: "blur(8px)",
              boxShadow: "0 -10px 24px rgba(0,0,0,0.06)",
              p: 1.5,
            }}
          >
            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={1}
              alignItems={{ sm: "center" }}
              justifyContent="space-between"
              sx={{ minWidth: 0 }}
            >
              <Box sx={{ minWidth: 0 }}>
                <Typography fontWeight={900} sx={{ lineHeight: 1.1 }}>
                  {changes.length === 0
                    ? "No changes"
                    : `${changes.length} change(s) pending`}
                </Typography>
                <Typography variant="caption" sx={{ color: "#6B7280" }}>
                  Save will audit your edits.
                </Typography>
              </Box>

              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={1}
                sx={{ width: { xs: "100%", sm: "auto" } }}
              >
                <Button
                  fullWidth={isMobile}
                  variant="outlined"
                  onClick={handleCancel}
                  disabled={saving}
                >
                  Cancel
                </Button>

                <Button
                  fullWidth={isMobile}
                  variant="contained"
                  onClick={handleSave}
                  disabled={saving || changes.length === 0 || !!successMessage}
                  sx={{
                    background: "#0EA5E9",
                    "&:hover": { background: "#0284C7" },
                  }}
                >
                  {saving ? "Saving…" : "Save"}
                </Button>
              </Stack>
            </Stack>
          </Paper>
        </Box>
      </Box>
    </Box>
  );
}
